import { useId, useState, type ReactNode } from 'react';
import { Checkbox } from './Checkbox.js';
import { Icon } from './Icon.js';
import { VisuallyHidden } from './VisuallyHidden.js';
import './DataTable.css';

export type CellAlign = 'start' | 'end' | 'center';

export interface DataColumn<Row> {
  /** Stable key. Also the `<th>`'s id when a header row is shown. */
  id: string;
  /** Column header text. Omit on every column to get the header-less shape. */
  header?: ReactNode;
  /**
   * How the column shares the row's width.
   *
   * A number is a proportional `flex-grow` (the prototype's `flex: 2 1 0`,
   * `1.4 1 0`, …); a px number as a string is a fixed basis (`flex: 0 0 120px`).
   * The **primary** column additionally gets a `min-inline-size`, because it is
   * what pushes the row to wrap on a narrow viewport — see the CSS note.
   */
  width?: number | string;
  align?: CellAlign;
  /**
   * Rendering treatment. These are the prototype's actual cell types, not generic
   * formatting hints:
   *
   *   `text`    default body text
   *   `primary` a semibold title with an optional note beneath — the cell that
   *             carries the row's identity, and the one that survives a wrap
   *   `mono`    a right-aligned mono identifier in muted text
   *   `numeric` tabular figures, semibold
   *   `slot`    render whatever the accessor returns, untouched (badges, chips)
   */
  kind?: 'text' | 'primary' | 'mono' | 'numeric' | 'slot';
  cell: (row: Row) => ReactNode;
  /** Second line under a `primary` cell. */
  note?: (row: Row) => ReactNode;
  /**
   * Makes this column sortable, by extracting the value to compare.
   *
   * **Opt-in per column, and only meaningful in the header-row shape** — a
   * header-less row list has nothing to click. Absence means not sortable, which
   * is the right default: a sortable-looking header that does nothing promises
   * assistive tech an interaction with no effect.
   *
   * Return `null`/`undefined` for "not knowable" (a BYO-Cloud tenant's source
   * count). Those sort **last in both directions** — see `sortRows`.
   */
  sortValue?: (row: Row) => string | number | null | undefined;
}

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  /** `DataColumn.id` of the sorted column. */
  id: string;
  direction: SortDirection;
}

export interface DataTableProps<Row> {
  columns: DataColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /**
   * What the table is for, e.g. "Sync runs". Required — a bare grid of divs
   * announces as unstructured text, and a screen-reader user landing mid-page has
   * no way to know a table is there.
   */
  label: string;
  /** The card's title strip. */
  title?: ReactNode;
  /** Right side of the title strip: a hint, a filter row, a count. */
  titleAside?: ReactNode;
  /** The `--surface-strip` band at the bottom — "Showing 7 of 11,400", an export action. */
  footer?: ReactNode;
  /**
   * Expandable rows. When provided, each row becomes a disclosure button and this
   * renders the expanded region. Only one row is open at a time, matching the
   * prototype: several open at once turns the list into a page.
   */
  expanded?: (row: Row) => ReactNode;
  /** Row selection. Controlled — the caller owns the set. */
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  /** Accessible name for a row's own checkbox, e.g. `(row) => row.name`. */
  selectLabel?: (row: Row) => string;
  /** Clicking a row navigates. Mutually exclusive with `expanded`. */
  onRowSelect?: (row: Row) => void;
  /**
   * What to show when `rows` is empty. **Required** — modular-components §7 rule 4
   * makes the empty state part of the component, not a follow-up ticket, and a
   * table that renders a bare header over nothing is the most common way that rule
   * gets skipped.
   */
  empty: ReactNode;
  /**
   * Which column the table starts sorted by. Ignored unless that column declares
   * a `sortValue`, so a stale `id` after a column rename degrades to unsorted
   * rather than throwing.
   */
  initialSort?: SortState;
}

/**
 * A column's width becomes a CSS **custom property**, not an inline `flex`.
 *
 * This is not stylistic. Column widths are per-column data, so they have to come
 * from the caller as an inline style — and an inline `flex` shorthand beats every
 * stylesheet rule, including the media query that makes the primary cell take a
 * full line on a phone. The first version did exactly that, and at 375px the row
 * kept its desktop proportions: a 75px identity cell beside 37px value cells,
 * which is unreadable. Passing the value through a custom property lets the
 * stylesheet stay in charge of *when* the proportions apply.
 */
/**
 * How much of the row a table's columns claim as **fixed px**, and therefore how early it
 * has to stop being a table.
 *
 * ⚠️ **A single global threshold was tried first and was wrong**, which is worth recording
 * because it looked reasonable. The container query (B-76/B-111) initially stacked every
 * table below 800px of container, on the argument that stacking is the safe direction. It is
 * not, when a table would have fitted: at a 768px viewport the review gallery's table is
 * 472px wide with only **140px** of fixed columns and three proportional ones, so it fits
 * comfortably — and stacking it threw away a header row for nothing. Two existing fidelity
 * tests said so, on linux, after the darwin runs I had done did not cover that width.
 *
 * The real condition is per-table: **stack when the container can no longer hold this
 * table's own fixed columns plus readable flexible ones.** Measured from the three real
 * cases —
 *
 *   - `TenantRegistry`: 630px fixed. Broken at 470px, fine at 972px → must stack early.
 *   - `AuditLog`: ~245px fixed. Verified tabular at 470px by B-110's fix → must NOT.
 *   - the gallery: 140px fixed → must NOT.
 *
 * — so the split sits at 400px of fixed demand, which separates the registry from the other
 * two with room on both sides. Two thresholds, not a continuum: a container query condition
 * cannot reference a custom property, so the value has to be in the stylesheet and the table
 * selects which block applies.
 */
const EARLY_STACK_FIXED_PX = 400;

export function fixedWidthTotal(columns: readonly DataColumn<never>[]): number {
  let total = 0;
  for (const col of columns) {
    // Only px strings are fixed; a number is a proportional share and costs no floor.
    if (typeof col.width === 'string') {
      const px = /^(\d+(?:\.\d+)?)px$/.exec(col.width.trim());
      if (px) total += Number(px[1]);
    }
  }
  return total;
}

function flexFor(width: DataColumn<unknown>['width']): string {
  if (width === undefined) return '1 1 0';
  if (typeof width === 'number') return `${width} 1 0`;
  return `0 0 ${width}`;
}

/**
 * Resolve a column's alignment — **one function, used by both the header cell and
 * the body cells.**
 *
 * The first version resolved these separately: the header defaulted to `start`
 * while numeric and mono cells defaulted to `end`. The result was visible
 * immediately in a browser and invisible to every test — a "COMPLETE" header sitting
 * left of its right-aligned percentages, and an "API NAME" header left of its
 * right-aligned mono ids. A column header that does not sit over its own column is
 * worse than no header, which is the same reasoning behind hiding the header row
 * entirely once cells wrap.
 */
function alignFor(col: Pick<DataColumn<unknown>, 'align' | 'kind'>): CellAlign {
  if (col.align) return col.align;
  return col.kind === 'numeric' || col.kind === 'mono' ? 'end' : 'start';
}

/**
 * Sort a copy of `rows`, stably, with unknown values last in both directions.
 *
 * **A copy, because `Array.prototype.sort` mutates**, and mutating a prop array
 * would make a parent's data depend on whether a child happened to render.
 *
 * **Nulls last in both directions, not "smallest".** A `null` here means *not
 * knowable* — the operator console's BYO-Cloud tenants have no observable source
 * count — so floating them to the top of a descending sort would present absence
 * as the extreme value, which is a claim about the data rather than a gap in it.
 */
function sortRows<Row>(rows: Row[], col: DataColumn<Row> | undefined, direction: SortDirection): Row[] {
  if (!col?.sortValue) return rows;
  const factor = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = col.sortValue!(a);
    const bv = col.sortValue!(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
    // localeCompare so accented names order the way a reader expects, rather than
    // by code point.
    return String(av).localeCompare(String(bv)) * factor;
  });
}

/**
 * The sectioned data table (design-system catalogue, Tier 3).
 *
 * **15 of the prototype's 18 page files, and the largest single build in the
 * catalogue** (prototype/CENSUS.md §2a item 4). Two shapes share one anatomy:
 * a header-row table (4 pages) and a header-less row list inside a sectioned card
 * (13 pages). Omit every column's `header` to get the second.
 *
 * **It is flex rows, not `<table>` — and that is a real trade, made deliberately.**
 * The prototype's entire mobile strategy is `flex-wrap` on the row plus a
 * `min-inline-size` on the primary cell: cells reflow onto a second line and the
 * table becomes readable at 375px with no separate card view and no duplicated
 * markup. A real `<table>` cannot do that — `display: block` on a table row throws
 * away the column model *and* the semantics, which is the worst of both.
 *
 * **The semantics therefore depend on whether the rows are interactive, because
 * these are genuinely two different objects.**
 *
 *   Plain or selectable rows → `role="table"` / `row` / `cell` / `columnheader`.
 *     A grid of values you read across. Assistive tech treats this identically to
 *     a native table.
 *
 *   Expandable or navigating rows → a real `<ul>` / `<li>`.
 *     A list of things you act on. The first draft kept the table roles here and
 *     axe rejected it correctly: `role="row"` may only contain cells, and wrapping
 *     the cells in the `<button>` that makes the whole row clickable violates that.
 *
 * The alternatives were worse. Putting the button inside one cell would leave the
 * rest of a 60px row inert while looking clickable — the exact defect the
 * whole-row control exists to avoid. `role="grid"` would fix the structure but
 * commits to full arrow-key cell navigation, which the prototype does not have and
 * which would be a promise the component cannot keep. A list is what these rows
 * *are*: a screen-reader user hears "list, 7 items, button: Today 09:14,
 * Completed, 11,400 records, collapsed", which is more useful than a broken grid.
 *
 * **Sorting is built, opt-in per column, and arrived with its owning feature.**
 * It appears nowhere in the prototype — sort order is stated there as prose in the
 * title strip ("sorted by identity weight") — so it was correctly left out of the
 * first build rather than invented. F30's tenant registry is the first screen that
 * genuinely needs it (an operator scanning the fleet for the oldest tenant, or the
 * one with the most members), which is the bar docs-v3.9 set: a capability lands
 * with the feature that can justify it. It is therefore deliberately minimal —
 * clickable `columnheader`s, `aria-sort` on the sorted one only, no multi-column
 * sort, no persistence — and only reachable in the header-row shape, because a
 * header-less row list has nothing to click. Columns without a `sortValue` render
 * as plain text, so nothing changes for the 13 prototype pages that never sort.
 *
 * **Still not built, on purpose:** pagination, virtualisation, column resizing.
 * The prototype states paging as a footer sentence ("Showing 7 of 11,400"), and
 * virtualisation would break F30's ui-spec requirement that a table report a
 * correct *total* row count rather than the rendered window — shipping the window
 * without the count would break that quietly.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  label,
  title,
  titleAside,
  footer,
  expanded,
  selectedIds,
  onToggleSelect,
  selectLabel,
  onRowSelect,
  empty,
  initialSort,
}: DataTableProps<Row>) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>(initialSort ?? null);
  const baseId = useId();
  const hasHeader = columns.some((c) => c.header !== undefined);
  const selectable = Boolean(selectedIds && onToggleSelect);
  /**
   * Rows wrapped in a whole-row button are a LIST, not a table — see the note
   * above. Derived rather than exposed as a prop, so a caller cannot pair
   * whole-row buttons with grid roles: that is the combination axe rejects, and
   * making it unreachable is better than documenting it as forbidden.
   */
  const isList = Boolean(expanded || onRowSelect);

  /* Sorting only exists where there are headers to click. A `sortValue` on a
     header-less column is inert rather than an error — the shape is chosen by
     omitting `header`, so this combination is easy to reach by accident. */
  const sortColumn = hasHeader ? columns.find((c) => c.id === sort?.id && c.sortValue) : undefined;
  const visibleRows = sortColumn ? sortRows(rows, sortColumn, sort!.direction) : rows;

  function toggleSort(id: string) {
    setSort((current) =>
      current?.id === id
        ? { id, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { id, direction: 'asc' }
    );
  }

  const cellsFor = (row: Row) =>
    columns.map((col) => {
      const kind = col.kind ?? 'text';
      const note = col.note?.(row);
      const align = alignFor(col);
      return (
        <span
          key={col.id}
          // Cells only carry the `cell` role in the table shape; inside a list row
          // they are plain spans, since a `cell` outside a `row` is invalid ARIA.
          {...(isList ? {} : { role: 'cell' as const })}
          className={`dpr-table__td dpr-table__td--${kind} dpr-table__cell--${align}`}
          style={{ '--dpr-col-flex': flexFor(col.width) } as React.CSSProperties}
        >
          <span className="dpr-table__td-main">{col.cell(row)}</span>
          {note ? <span className="dpr-table__td-note">{note}</span> : null}
        </span>
      );
    });

  const head = hasHeader ? (
    <div className="dpr-table__head" role="row">
      {/* Utility columns get real, visually-hidden names rather than being empty.
          axe flags an empty `columnheader` and is right to: a screen-reader user
          navigating by column hears nothing where the header should be, so the
          column reads as unlabelled data. */}
      {selectable ? (
        <span className="dpr-table__select-cell" role="columnheader">
          <VisuallyHidden>Select</VisuallyHidden>
        </span>
      ) : null}
      {columns.map((col) => {
        const isSorted = sortColumn?.id === col.id;
        return (
          <span
            key={col.id}
            role="columnheader"
            /* `aria-sort` on the sorted column ONLY. Setting `none` on every other
               header is valid but announces noise on each one a user moves
               through, and the absence already means unsorted. */
            aria-sort={isSorted ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
            /* The `kind` goes on the HEADER cell too, not just the body cell.
               Any width constraint a column carries has to apply to both or the two
               rows compute different columns and the labels drift off their values —
               which is what happened when only `td--slot` got a `min-content` floor:
               State drifted 74px from its own column. */
            className={`dpr-table__th dpr-table__th--${col.kind ?? 'text'} dpr-table__cell--${alignFor(col)}`}
            style={{ '--dpr-col-flex': flexFor(col.width) } as React.CSSProperties}
          >
            {col.sortValue ? (
              <button
                type="button"
                className="dpr-table__sort"
                data-sorted={isSorted ? sort!.direction : undefined}
                onClick={() => toggleSort(col.id)}
              >
                <span>{col.header}</span>
                {/* The state is carried by `aria-sort`, so the chevron is purely
                    visual — an icon from the set rather than a ▲/▼ dingbat, which
                    the glyph-icon lint bans and which would not match the 1.7px
                    stroke of everything else. Rotated by CSS for ascending. */}
                <Icon name="chevronDown" size={14} className="dpr-table__sort-icon" />
              </button>
            ) : (
              col.header
            )}
          </span>
        );
      })}
    </div>
  ) : null;

  const body = visibleRows.map((row) => {
    const id = rowKey(row);
    const isOpen = openId === id;
    const rowClass = [
      'dpr-table__row',
      isOpen ? 'dpr-table__row--open' : '',
      selectedIds?.has(id) ? 'dpr-table__row--selected' : '',
      isList ? 'dpr-table__row--interactive' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const selectCell = selectable ? (
      <span className="dpr-table__select-cell" {...(isList ? {} : { role: 'cell' as const })}>
        <Checkbox
          checked={selectedIds!.has(id)}
          onChange={() => onToggleSelect!(id)}
          label={selectLabel?.(row) ?? `Select row ${id}`}
          hideLabel
        />
      </span>
    ) : null;

    if (!isList) {
      return (
        <div className="dpr-table__row-group" key={id}>
          <div className={rowClass} role="row">
            {selectCell}
            {cellsFor(row)}
          </div>
        </div>
      );
    }

    return (
      <li className="dpr-table__row-group" key={id}>
        <div className={rowClass}>
          {selectCell}
          {/* The row IS the control, as in the prototype — a separate chevron
              button would leave most of a 60px-tall row inert while looking
              clickable. */}
          <button
            type="button"
            className="dpr-table__row-button"
            {...(expanded
              ? {
                  'aria-expanded': isOpen,
                  'aria-controls': `${baseId}-${id}`,
                  onClick: () => setOpenId(isOpen ? null : id),
                }
              : { onClick: () => onRowSelect!(row) })}
          >
            {cellsFor(row)}
            {expanded ? (
              <span className="dpr-table__chevron-cell" aria-hidden="true">
                {isOpen ? '▲' : '▼'}
              </span>
            ) : null}
          </button>
        </div>
        {expanded && isOpen ? (
          <div className="dpr-table__expanded" id={`${baseId}-${id}`}>
            {expanded(row)}
          </div>
        ) : null}
      </li>
    );
  });

  const emptyBlock = <div className="dpr-table__empty">{empty}</div>;

  return (
    <section
      className="dpr-table"
      aria-label={label}
      /* Which container threshold applies. An attribute rather than a class so the two
         stacking blocks in DataTable.css read as one decision with two branches. */
      data-stack={
        fixedWidthTotal(columns as readonly DataColumn<never>[]) > EARLY_STACK_FIXED_PX
          ? 'early'
          : 'late'
      }
    >
      {title || titleAside ? (
        <header className="dpr-table__strip">
          {title ? <h3 className="dpr-table__title">{title}</h3> : null}
          {titleAside ? <div className="dpr-table__aside">{titleAside}</div> : null}
        </header>
      ) : null}

      {rows.length === 0 ? (
        emptyBlock
      ) : isList ? (
        <ul className="dpr-table__list">{body}</ul>
      ) : (
        <div role="table" aria-label={label}>
          {head}
          {body}
        </div>
      )}

      {footer ? <footer className="dpr-table__footer">{footer}</footer> : null}
    </section>
  );
}
